import { type ReactNode, useCallback, useEffect, useMemo } from 'react';
import { ConfirmBridge } from './confirm';
import { type LayerStack, getLayerStack } from './stack';
import type { LayerNavigationEvent, LayerNavigationSource } from './types';
import { LayerStackProvider, useLayerStack } from './useLayer';

export type LayerRootProps = {
  children: ReactNode;
  /**
   * Route changes close every non-pinned layer (pathname change or browser back / forward). The app
   * adapts its router; `@dataroom/ui` stays router-agnostic.
   */
  navigation?: LayerNavigationSource;
  /** Defaults to the document's stack. */
  stack?: LayerStack;
};

/**
 * Mount once at the root (`__root.tsx`). Owns `#dr-layers` (z `--z-layers`, DOM order = stacking
 * order) and `#dr-system` (z `--z-system`: toasts), the Escape router, route-change closing and the
 * confirm host (`useConfirm`, discard guards).
 */
export function LayerRoot({ children, navigation, stack: given }: LayerRootProps) {
  const stack = useMemo(() => given ?? getLayerStack(), [given]);
  useEffect(() => {
    stack.container('layers');
    stack.container('system');
    stack.ensureGlobalListeners();
  }, [stack]);
  useEffect(() => {
    if (!navigation) return;
    return navigation.subscribe((event) => stack.closeForNavigation(event));
  }, [navigation, stack]);
  return (
    <LayerStackProvider stack={stack}>
      {children}
      <ConfirmBridge />
    </LayerStackProvider>
  );
}

/**
 * Router glue for dirty layers: `confirmNavigation` resolves false when the member chose "Keep
 * editing" (block), `shouldBlockUnload` arms the browser's leave prompt only while something is dirty.
 */
export function useLayerNavigationGuard() {
  const stack = useLayerStack();
  const confirmNavigation = useCallback(
    (event: LayerNavigationEvent) => stack.confirmNavigation(event),
    [stack],
  );
  const shouldBlockUnload = useCallback(() => stack.shouldBlockUnload(), [stack]);
  return { confirmNavigation, shouldBlockUnload };
}
